import api from '../../../utils/api'
import { useState, useEffect } from 'react'
import { FaMoneyBillTrendUp } from 'react-icons/fa6'

import Button from '../../../shared/components/Button/Button'
import TimestampToString from '../../../shared/functions/TimestampToString'
import useMyPermission from '../../../hooks/useMyPermission'

import './OrderProductionTree.css'

function itemLabel(item) {
    if (!item) return 'Item'
    const parts = []
    if (item.category) parts.push(item.category.label || item.category.name)
    if (item.cloth) parts.push(item.cloth.label || item.cloth.name)
    if (item.sex) parts.push(item.sex.label)
    const text = parts.filter(Boolean).join(' · ')
    return item.simpleProduct ? item.description : (text || item.description || 'Item')
}

function packageStatusLabel(status) {
    if (status === 'em_transito') return 'Em trânsito'
    if (status === 'recebido') return 'Recebido'
    return 'Finalizado'
}

function purchaseStatusLabel(status) {
    if (status === 'recebido') return 'Recebido'
    if (status === 'cancelado') return 'Cancelado'
    return 'Aguardando'
}

// Mesmo vocabulário/cores de PURCHASE_STATUSES em Purchase.js — o pedido carrega esse
// sub-status em order.status enquanto está em Compras.
function compraStatusClass(status) {
    switch (status) {
        case 'Aguardando Compra': return 'optree-node-pill-compra-pendente'
        case 'Pedido de Compra Realizado': return 'optree-node-pill-compra-aguardando'
        case 'Separado do Estoque': return 'optree-node-pill-compra-separado'
        case 'Pronto para Produção': return 'optree-node-pill-compra-pronto'
        default: return ''
    }
}

// Mesmo shortId usado só pra exibição — o _id completo vai na ficha, isso aqui é só o
// "código visível" da caixa na árvore.
function shortId(id) {
    return id ? String(id).slice(-6) : '—'
}

// Data curta (sem hora) pra caber no card — a ficha lateral mostra a data/hora completa.
function shortDate(d) {
    return d ? new Date(d).toLocaleDateString('pt-BR') : '—'
}

function OrderProductionTree({ orderId, btCancelAction }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const [load, setLoad] = useState(false)
    const { timeStampToDDMMAAAA } = TimestampToString()

    const [order, setOrder] = useState(null)
    const [purchaseOrders, setPurchaseOrders] = useState([])
    const [productionOrders, setProductionOrders] = useState([])
    const [packages, setPackages] = useState([])

    const [selected, setSelected] = useState({ kind: 'orcamento', id: 'orcamento' })

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionViewMargin = checkPermission(myPermission, 'order_viewMargin')

    useEffect(() => {
        api.get(`/production/order/${orderId}/tree`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => {
                setOrder(response.data.order)
                setPurchaseOrders(response.data.purchaseOrders || [])
                setProductionOrders(response.data.productionOrders || [])
                setPackages(response.data.packages || [])
                setMyPermission(response.data.permission)
                setSelected({ kind: 'orcamento', id: 'orcamento' })
                setLoad(true)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, token])

    if (!load) {
        return <div className="pg-loading">Carregando árvore de produção...</div>
    }

    function selectNode(kind, id) {
        setSelected({ kind, id })
    }
    function isSelected(kind, id) {
        return selected.kind === kind && String(selected.id) === String(id)
    }
    function nodeCls(kind, id, extra) {
        return `optree-node${extra ? ` ${extra}` : ''}${isSelected(kind, id) ? ' optree-node-selected' : ''}`
    }
    function nodeA11y(kind, id) {
        return {
            role: 'button',
            tabIndex: 0,
            onClick: () => selectNode(kind, id),
            onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectNode(kind, id) } },
        }
    }

    const selectedOp = productionOrders.find((po) => po._id === selected.id)
    const selectedPackage = packages.find((p) => p._id === selected.id)

    // Caixa de pacote + filhos (recursivo) — bifurca sempre que 2+ pacotes têm o mesmo
    // parentPackage, exatamente como o backend grava (ProductionPackage.parentPackage).
    function renderPackageNode(pkg) {
        const children = packages.filter((p) => p.parentPackage === pkg._id)
        const displayQty = pkg.status === 'em_transito'
            ? pkg.sentQuantity
            : (pkg.remainingQuantity ?? pkg.receivedQuantity ?? pkg.sentQuantity)

        return (
            <li className="optree-branch" key={pkg._id}>
                <div className={nodeCls('package', pkg._id)} data-status={pkg.status} {...nodeA11y('package', pkg._id)}>
                    <span className="optree-node-id">pkg_{shortId(pkg._id)}{!pkg.parentPackage ? ' · raiz' : ''}</span>
                    <span className="optree-node-route">{pkg.fromSector || 'Estoque'}<span className="optree-node-arrow">→</span>{pkg.toSector}</span>
                    <span className="optree-node-qty">{displayQty} {pkg.unit?.label || pkg.unit?.value}</span>
                    <span className="optree-node-date">Enviado em {shortDate(pkg.sentAt)}{pkg.receivedAt ? ` · Recebido em ${shortDate(pkg.receivedAt)}` : ''}</span>
                    <span className={`optree-node-pill optree-node-pill-${pkg.status}`}>{packageStatusLabel(pkg.status)}</span>
                    {children.length > 1 && <span className="optree-node-split">dividido em {children.length}</span>}
                    {pkg.obs && <span className="optree-node-obs">{pkg.obs}</span>}
                </div>
                {children.length > 0 && (
                    <ul className="optree-children">
                        {children.map((c) => renderPackageNode(c))}
                    </ul>
                )}
            </li>
        )
    }

    function renderOpNode(po) {
        const root = packages.find((p) => p.productionOrder === po._id && !p.parentPackage)
        return (
            <li className="optree-branch" key={po._id}>
                <div className={nodeCls('op', po._id, 'optree-node-op')} {...nodeA11y('op', po._id)}>
                    <span className="optree-node-id">{po.code}</span>
                    <span className="optree-node-route">{itemLabel(po.itemSnapshot)}</span>
                    <span className="optree-node-qty">{po.quantity} un. esperadas</span>
                    <span className="optree-node-date">Criada em {shortDate(po.createdAt)}</span>
                    <span className={`optree-node-pill ${po.materialConsumed ? 'optree-node-pill-finalizado' : 'optree-node-pill-em_transito'}`}>
                        {po.materialConsumed ? 'Consumo fechado' : 'Em produção'}
                    </span>
                </div>
                {root && (
                    <ul className="optree-children">
                        {renderPackageNode(root)}
                    </ul>
                )}
            </li>
        )
    }

    const totalCosts = productionOrders.reduce((acc, po) => acc + (po.costs || []).reduce((a, c) => a + c.value, 0), 0)
    const latestPurchaseOrder = purchaseOrders[purchaseOrders.length - 1]
    const orcamentoDate = order.orderEntryDate ? timeStampToDDMMAAAA(order.orderEntryDate) : shortDate(order.createdAt)

    return (
        <div className="optree">
            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Árvore de Produção — #{order.code}</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <p className="optree-subtitle">{order.client?.name} · do orçamento até {productionOrders.length > 0 ? `${productionOrders.length} ${productionOrders.length === 1 ? 'Ordem de Produção' : 'Ordens de Produção'}` : 'a Produção'}</p>
                        {myPermissionViewMargin && (
                            <Button variant="save" icon={FaMoneyBillTrendUp} onClick={() => window.open(`/order/margin/${orderId}`, '_blank')}>Ver Lucro / Margem</Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="optree-body">
            <div className="optree-workspace">
                <div className="optree-canvas-wrap">
                    <div className="optree-canvas">
                        <ul className="optree-tree">
                            <li className="optree-branch">
                                <div className={nodeCls('orcamento', 'orcamento', 'optree-node-orcamento')} {...nodeA11y('orcamento', 'orcamento')}>
                                    <span className="optree-node-id">#{order.code} · raiz</span>
                                    <span className="optree-node-route">Orçamento aprovado</span>
                                    <span className="optree-node-qty">{order.client?.name}</span>
                                    <span className="optree-node-date">Entrada em {orcamentoDate}</span>
                                    <span className="optree-node-pill optree-node-pill-orcamento">R$ {order.orderPrice || order.totalPrice}</span>
                                </div>
                                <ul className="optree-children">
                                    <li className="optree-branch">
                                        <div className={nodeCls('compra', 'compra', 'optree-node-compra')} {...nodeA11y('compra', 'compra')}>
                                            <span className="optree-node-id">Compras</span>
                                            <span className="optree-node-route">{order.step}</span>
                                            <span className="optree-node-qty">{purchaseOrders.length} {purchaseOrders.length === 1 ? 'Pedido de Compra' : 'Pedidos de Compra'}</span>
                                            <span className="optree-node-date">{latestPurchaseOrder ? `Último em ${shortDate(latestPurchaseOrder.createdAt)}` : `Atualizado em ${shortDate(order.updatedAt)}`}</span>
                                            <span className={`optree-node-pill ${compraStatusClass(order.status)}`}>{order.status}</span>
                                        </div>
                                        {productionOrders.length > 0 && (
                                            <ul className="optree-children">
                                                {productionOrders.map((po) => renderOpNode(po))}
                                            </ul>
                                        )}
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    {productionOrders.length === 0 && (
                        <p className="optree-empty">Esse pedido ainda não tem nenhuma Ordem de Produção — nascem automaticamente quando o pedido é gerado a partir do orçamento.</p>
                    )}
                </div>

                <div className="optree-detail">
                    <p className="optree-detail-title">Ficha {selected.kind === 'orcamento' ? 'do orçamento' : selected.kind === 'compra' ? 'da compra' : selected.kind === 'op' ? 'da OP' : 'do pacote'}</p>

                    {selected.kind === 'orcamento' && (
                        <div className="optree-detail-fields">
                            <div className="optree-field"><span className="optree-field-key">Código</span><span className="optree-field-val">#{order.code}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Cliente</span><span className="optree-field-val">{order.client?.name}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Vendedor</span><span className="optree-field-val">{order.seller?.name || '—'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Valor</span><span className="optree-field-val">R$ {order.orderPrice || order.totalPrice}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Entrada do pedido</span><span className="optree-field-val">{order.orderEntryDate ? timeStampToDDMMAAAA(order.orderEntryDate) : '—'}</span></div>
                        </div>
                    )}

                    {selected.kind === 'compra' && (
                        <div className="optree-detail-fields">
                            <div className="optree-field"><span className="optree-field-key">Etapa</span><span className="optree-field-val">{order.step}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Status</span><span className="optree-field-val">{order.status}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Pedidos de Compra</span><span className="optree-field-val">{purchaseOrders.length}</span></div>
                            {purchaseOrders.length > 0 && (
                                <div className="optree-po-list">
                                    {purchaseOrders.map((po) => (
                                        <div key={po._id} className="optree-po-item">
                                            <span className="optree-po-provider">{po.provider?.name}</span>
                                            <span className={`optree-po-status optree-po-status-${po.status}`}>{purchaseStatusLabel(po.status)}</span>
                                            <span className="optree-po-value">R$ {po.totalValue.toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {selected.kind === 'op' && selectedOp && (
                        <div className="optree-detail-fields">
                            <div className="optree-field"><span className="optree-field-key">Código</span><span className="optree-field-val">{selectedOp.code}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Item</span><span className="optree-field-val">{itemLabel(selectedOp.itemSnapshot)}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Quantidade esperada</span><span className="optree-field-val">{selectedOp.quantity} un.</span></div>
                            <div className="optree-field"><span className="optree-field-key">Consumo de material</span><span className="optree-field-val">{selectedOp.materialConsumed ? 'Fechado' : 'Em aberto'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Custos lançados</span><span className="optree-field-val">R$ {(selectedOp.costs || []).reduce((a, c) => a + c.value, 0).toFixed(2)}</span></div>
                        </div>
                    )}

                    {selected.kind === 'package' && selectedPackage && (
                        <div className="optree-detail-fields">
                            <div className="optree-field"><span className="optree-field-key">Ordem de Produção</span><span className="optree-field-val">{productionOrders.find((po) => po._id === selectedPackage.productionOrder)?.code}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Pacote de origem</span><span className="optree-field-val">{selectedPackage.parentPackage ? `pkg_${shortId(selectedPackage.parentPackage)}` : '— (raiz)'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Unidade</span><span className="optree-field-val">{selectedPackage.unit?.label || selectedPackage.unit?.value}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Setor de origem</span><span className="optree-field-val">{selectedPackage.fromSector || '— (Estoque)'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Setor de destino</span><span className="optree-field-val">{selectedPackage.toSector}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Quantidade enviada</span><span className="optree-field-val">{selectedPackage.sentQuantity}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Enviado em</span><span className="optree-field-val">{new Date(selectedPackage.sentAt).toLocaleString('pt-BR')}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Enviado por</span><span className="optree-field-val">{selectedPackage.sentBy?.name || '—'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Quantidade recebida</span><span className="optree-field-val">{selectedPackage.receivedQuantity ?? '— aguardando'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Recebido em</span><span className="optree-field-val">{selectedPackage.receivedAt ? new Date(selectedPackage.receivedAt).toLocaleString('pt-BR') : '—'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Recebido por</span><span className="optree-field-val">{selectedPackage.receivedBy?.name || '—'}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Status</span><span className={`optree-field-val optree-field-status-${selectedPackage.status}`}>{packageStatusLabel(selectedPackage.status)}</span></div>
                            <div className="optree-field"><span className="optree-field-key">Observação</span><span className="optree-field-val">{selectedPackage.obs || '—'}</span></div>
                        </div>
                    )}
                </div>
            </div>

            {totalCosts > 0 && (
                <p className="optree-total-costs">Total de custos lançados na produção deste pedido: R$ {totalCosts.toFixed(2)}</p>
            )}

            {btCancelAction && (
                <div className="optree-footer">
                    <Button variant="cancel" onClick={btCancelAction}>Fechar</Button>
                </div>
            )}
            </div>
        </div>
    )
}

export default OrderProductionTree
